import { useCallback, useEffect, useState } from 'react';
import PageHeader from '../components/PageHeader';
import { useBusiness } from '../context/BusinessContext';
import { api } from '../api/client';

const blankKeys = { stripe_pub_key: '', stripe_secret: '', stripe_webhook_secret: '' };

export default function StripeSettings() {
  const { activeId, businesses } = useBusiness();
  const biz = (businesses || []).find((b) => b.id === activeId);

  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editEnv, setEditEnv] = useState('test');      // which env's keys are being edited
  const [form, setForm] = useState({ stripe_mode: 'originating', stripe_account_id: '', ...blankKeys });
  const [clearSecret, setClearSecret] = useState(false);
  const [clearWebhook, setClearWebhook] = useState(false);
  const [saving, setSaving] = useState(false);
  const [switching, setSwitching] = useState(false);
  const [notice, setNotice] = useState(null);
  const [error, setError] = useState(null);

  // Repopulate the editable form for the chosen environment from the loaded status.
  const fillForm = useCallback((s, env) => {
    setForm({
      stripe_mode: s.mode || 'none',
      stripe_account_id: s.account_id || '',
      stripe_pub_key: (s[env] && s[env].pub_key) || '',
      stripe_secret: '', stripe_webhook_secret: '',
    });
    setClearSecret(false); setClearWebhook(false);
  }, []);

  const load = useCallback(() => {
    setLoading(true); setNotice(null); setError(null);
    api(`/businesses/${activeId}/stripe`)
      .then((s) => { setStatus(s); const env = s.env || 'test'; setEditEnv(env); fillForm(s, env); })
      .catch(() => setError('Could not load Stripe settings.'))
      .finally(() => setLoading(false));
  }, [activeId, fillForm]);

  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const chooseEnv = (env) => { setEditEnv(env); if (status) fillForm(status, env); setNotice(null); setError(null); };

  async function switchActive(env) {
    if (!status || status.env === env || switching) return;
    setSwitching(true); setError(null); setNotice(null);
    try {
      const s = await api(`/businesses/${activeId}/stripe`, { method: 'PUT', body: { set_active: env } });
      setStatus(s);
      setNotice(`Now using ${env === 'live' ? 'LIVE' : 'TEST'} keys for ${biz?.name || 'this business'}.`);
    } catch (e) { setError(e?.detail || 'Could not switch environment.'); }
    finally { setSwitching(false); }
  }

  async function save() {
    setSaving(true); setNotice(null); setError(null);
    const body = {
      env: editEnv,
      stripe_mode: form.stripe_mode,
      stripe_account_id: form.stripe_account_id,
      stripe_pub_key: form.stripe_pub_key,
    };
    if (form.stripe_secret.trim()) body.stripe_secret = form.stripe_secret.trim();
    else if (clearSecret) body.clear_secret = true;
    if (form.stripe_webhook_secret.trim()) body.stripe_webhook_secret = form.stripe_webhook_secret.trim();
    else if (clearWebhook) body.clear_webhook_secret = true;
    try {
      const s = await api(`/businesses/${activeId}/stripe`, { method: 'PUT', body });
      setStatus(s); fillForm(s, editEnv);
      setNotice(`${editEnv === 'live' ? 'Live' : 'Test'} keys saved.`);
    } catch (e) { setError(e?.detail || 'Could not save Stripe settings.'); }
    finally { setSaving(false); }
  }

  const envStatus = status && status[editEnv];
  const webhookUrl = `${window.location.origin}/api/stripe/webhook/${activeId}`;

  return (
    <>
      <PageHeader title="Payments (Stripe)" />
      <section className="panel panel--pad" style={{ maxWidth: 660 }}>
        <p className="hint" style={{ marginTop: 0 }}>
          Configuring <strong>{biz?.name || `business #${activeId}`}</strong>. Each business has its own Stripe
          account and keys; switch businesses using the selector in the top bar. Secret keys are encrypted at rest.
        </p>

        {loading ? <div className="hint">Loading…</div> : (
          <>
            {/* Active environment switch */}
            <div className="stripe-env">
              <div className="stripe-env__row">
                <span className="stripe-env__label">Active environment</span>
                {status?.livemode
                  ? <span className="env-pill env-pill--live">● Live</span>
                  : <span className="env-pill env-pill--test">● Test</span>}
              </div>
              <div className="segmented" role="tablist" aria-label="Active Stripe environment">
                <button className={'segmented__opt' + (!status?.livemode ? ' segmented__opt--on' : '')}
                  disabled={switching} onClick={() => switchActive('test')}>Test</button>
                <button className={'segmented__opt' + (status?.livemode ? ' segmented__opt--on segmented__opt--live' : '')}
                  disabled={switching} onClick={() => switchActive('live')}>Live</button>
              </div>
              <p className="hint" style={{ margin: '8px 0 0' }}>
                The app uses the <strong>{status?.livemode ? 'live' : 'test'}</strong> keys for all charges, saved
                payment methods, and webhook verification. Switching here takes effect immediately — no real money
                moves in test mode.
              </p>
            </div>

            {/* Per-environment credentials */}
            <h3 className="form-section" style={{ marginTop: 22 }}>Keys</h3>
            <div className="segmented segmented--sm" style={{ marginBottom: 12 }}>
              <button className={'segmented__opt' + (editEnv === 'test' ? ' segmented__opt--on' : '')} onClick={() => chooseEnv('test')}>
                Test keys {status?.test?.has_secret && <span className="dot dot--ok" />}
              </button>
              <button className={'segmented__opt' + (editEnv === 'live' ? ' segmented__opt--on segmented__opt--live' : '')} onClick={() => chooseEnv('live')}>
                Live keys {status?.live?.has_secret && <span className="dot dot--ok" />}
              </button>
            </div>

            <div className="field"><label>Publishable key</label>
              <input value={form.stripe_pub_key} onChange={(e) => set('stripe_pub_key', e.target.value)}
                placeholder={editEnv === 'live' ? 'pk_live_…' : 'pk_test_…'} /></div>

            <div className="field">
              <label>Secret key {envStatus?.has_secret && <span className="stored-tag">stored</span>}</label>
              <input type="password" autoComplete="off" value={form.stripe_secret}
                onChange={(e) => set('stripe_secret', e.target.value)}
                placeholder={envStatus?.has_secret ? 'Leave blank to keep current key' : (editEnv === 'live' ? 'sk_live_…' : 'sk_test_…')} />
              {envStatus?.has_secret && !form.stripe_secret.trim() && (
                <label className="checkrow" style={{ marginTop: 6 }}>
                  <input type="checkbox" checked={clearSecret} onChange={(e) => setClearSecret(e.target.checked)} />
                  <span>Remove the stored {editEnv} secret key</span>
                </label>
              )}
            </div>

            <div className="field">
              <label>Webhook signing secret {envStatus?.has_webhook_secret && <span className="stored-tag">stored</span>}</label>
              <input type="password" autoComplete="off" value={form.stripe_webhook_secret}
                onChange={(e) => set('stripe_webhook_secret', e.target.value)}
                placeholder={envStatus?.has_webhook_secret ? 'Leave blank to keep current' : 'whsec_…'} />
              {envStatus?.has_webhook_secret && !form.stripe_webhook_secret.trim() && (
                <label className="checkrow" style={{ marginTop: 6 }}>
                  <input type="checkbox" checked={clearWebhook} onChange={(e) => setClearWebhook(e.target.checked)} />
                  <span>Remove the stored {editEnv} webhook secret</span>
                </label>
              )}
              <p className="hint" style={{ marginTop: 6 }}>
                Add a {editEnv === 'live' ? 'live' : 'test'}-mode webhook in Stripe pointing at this endpoint, then paste its signing secret here:<br />
                <code className="endpoint">{webhookUrl}</code>
              </p>
            </div>

            <h3 className="form-section">Routing</h3>
            <div className="field">
              <label>Mode</label>
              <select value={form.stripe_mode} onChange={(e) => set('stripe_mode', e.target.value)}>
                <option value="originating">Originating — Flightdeck invoices &amp; charges here</option>
                <option value="reflected">Reflected — invoicing lives elsewhere; import only</option>
                <option value="none">None — Stripe disabled</option>
              </select>
            </div>
            <div className="field"><label>Account ID (optional)</label>
              <input value={form.stripe_account_id} onChange={(e) => set('stripe_account_id', e.target.value)} placeholder="acct_…" /></div>

            <div className="status-line">
              {status?.enabled
                ? <span className="badge badge--green">Connected · {status.livemode ? 'Live' : 'Test'}</span>
                : <span className="badge badge--slate">Not connected{status && !status[status.env]?.has_secret ? ` — no ${status.env} secret key` : ''}</span>}
            </div>

            {error && <div className="form-error">{error}</div>}
            {notice && <div className="notice" style={{ marginTop: 12 }}>{notice}<button className="notice__x" onClick={() => setNotice(null)}>×</button></div>}

            <div style={{ marginTop: 16 }}>
              <button className="btn btn--primary" onClick={save} disabled={saving}>
                {saving ? 'Saving…' : `Save ${editEnv} keys`}
              </button>
            </div>
          </>
        )}
      </section>
    </>
  );
}
